'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import urlMapping from '@next-app/config/urlMapping';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSessionInfo,
  selectUserTypeLoginStatus,
} from '@next-app/lib/features/InitSelectors';
import { setUserTypeLoginStatus } from '@next-app/lib/store';
import { LoginStatus } from '@next-app/constants/constants';

declare global {
  interface Window {
    refreshIdleTimerOnApiCall?: () => void;
  }
}

const SessionManager: React.FC = () => {
  const dispatch = useDispatch();

  const { maxSessionExpiryTime } = useSelector(selectSessionInfo) || {};
  const { userType, loginStatus } = useSelector(selectUserTypeLoginStatus);
  const lastApiCallRef = useRef(Date.now());
  const hasBeenIdleRef = useRef(false);

  const onIdle = useCallback(() => {
    console.log('User is idle');
    hasBeenIdleRef.current = true;
    if (loginStatus == LoginStatus.LOGGED_IN) {
      dispatch(
        setUserTypeLoginStatus({
          userType: userType,
          loginStatus: LoginStatus.RECOGNIZED,
        }),
      );
    }
  }, [userType, loginStatus]);

  const idleTimer = useIdleTimer({
    timeout: 1000 * maxSessionExpiryTime,
    onIdle,
    debounce: 500,
    events: [],
    startOnMount: true,
  });

  const getUrlFromFetchArgs = (args: any[]): string => {
    if (typeof args[0] === 'string') return args[0];
    if (args[0] instanceof URL) return args[0].toString();
    if (args[0] instanceof Request) return args[0].url;
    return '';
  };

  // Detect API calls via fetch and XMLHttpRequest
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      // Only detect API calls to your domain
      const url = getUrlFromFetchArgs(args);
      if (url && url.includes(window.location.origin)) {
        lastApiCallRef.current = Date.now();
        hasBeenIdleRef.current = false;
        console.log('API call detected via fetch. Resetting idle timer.');
        idleTimer.reset();
      }
      return originalFetch.apply(window, args);
    };

    const originalXhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) {
      // Only detect API calls to your domain
      const urlStr = typeof url === 'string' ? url : url.toString();
      if (urlStr.includes(window.location.origin)) {
        this.addEventListener('loadend', () => {
          lastApiCallRef.current = Date.now();
          hasBeenIdleRef.current = false;
          console.log('API call detected via fetch. Resetting idle timer.');
          idleTimer.reset();
        });
      }
      return originalXhrOpen.apply(this, arguments as any);
    };

    return () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXhrOpen;
    };
  }, [idleTimer]);

  // Detect route changes
  useEffect(() => {
    const handleRouteChange = () => {
      lastApiCallRef.current = Date.now();
      hasBeenIdleRef.current = false;
      console.log('Route change detected. Resetting idle timer.');
      idleTimer.reset();
    };
    // For next/navigation, you may need to use router.events if available
    // If not, you can listen for popstate
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [idleTimer]);

  // Mouse activity handler: refresh session only if backend expiry window has passed
  const onMouseActivity = useCallback(() => {
    const now = Date.now();
    const elapsed = now - lastApiCallRef.current;
    if (
      !hasBeenIdleRef.current &&
      maxSessionExpiryTime &&
      elapsed >= maxSessionExpiryTime * 1000
    ) {
      // Direct fetch call to avoid circular dependency with makeFetchCall
      fetch(urlMapping.sessionConfirmationNumberUrl)
        .then((response) => response.json())
        .catch((error) => console.error('Error refreshing session:', error));
      lastApiCallRef.current = now;
    }
    idleTimer.reset();
  }, [idleTimer, maxSessionExpiryTime]);

  useEffect(() => {
    const mouseEvents = [
      'mousemove',
      'mousedown',
      'mouseenter',
      'mouseleave',
      'mouseover',
      'mouseout',
    ];
    mouseEvents.forEach((event) =>
      window.addEventListener(event, onMouseActivity),
    );
    return () => {
      mouseEvents.forEach((event) =>
        window.removeEventListener(event, onMouseActivity),
      );
    };
  }, [onMouseActivity]);

  return null;
};

export default SessionManager;
